import { getApiUrl } from 'config/appConfig';
import { resolveStoreMediaUrl } from 'utils/themeDisplay';

/** Imagen local cuando el producto no tiene foto y no hay una personalizada. */
export const DEFAULT_PRODUCT_PLACEHOLDER = '/assets/sin_foto.png';

export const FALLBACK_PRODUCT_IMAGE = DEFAULT_PRODUCT_PLACEHOLDER;

let configuredPlaceholderUrl = '';

/** La define el tema de la tienda. Vacío vuelve a la imagen predeterminada. */
export const setConfiguredProductPlaceholder = (url?: string | null): void => {
  configuredPlaceholderUrl = url?.trim() ?? '';
};

const isBlank = (value?: string | null): boolean => !value || !value.trim();

const isDefaultPlaceholder = (src: string): boolean =>
  src.includes('/assets/sin_foto.png') || src.endsWith('sin_foto.png');

/** URL efectiva: la configurada en Apariencia, o la predeterminada de la tienda. */
export const getFallbackProductImage = (): string => {
  const trimmed = configuredPlaceholderUrl;
  if (!trimmed || trimmed.startsWith('/assets/')) return trimmed || DEFAULT_PRODUCT_PLACEHOLDER;
  return resolveStoreMediaUrl(trimmed) || DEFAULT_PRODUCT_PLACEHOLDER;
};

/**
 * medium y thumbnail se generan recortadas a cuadrado.
 * Tarjetas y ficha deben mostrar la foto original (full).
 */
export const toFullProductPhotoUrl = (imageUrl?: string | null): string => {
  const trimmed = imageUrl?.trim() ?? '';
  if (!trimmed) return '';

  const variant = trimmed.replace(/\/(?:medium|thumbnail)(?=(?:\?|#|$))/i, '/full');
  if (variant !== trimmed) return variant;

  if (/\/imagen(?=(?:\?|#|$))/i.test(trimmed) && !/[?&]size=/i.test(trimmed)) {
    return trimmed.includes('?') ? `${trimmed}&size=full` : `${trimmed}?size=full`;
  }

  return trimmed;
};

/** Resuelve URL de imagen; si falta o es inválida, usa el placeholder configurado. */
export const getProductImageUrl = (imageUrl?: string | null, _codigo?: string | null): string => {
  if (isBlank(imageUrl)) return getFallbackProductImage();

  const trimmed = imageUrl!.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
    return trimmed;
  }
  if (trimmed.startsWith('/')) {
    const base = getApiUrl().replace(/\/$/, '');
    return `${base}${trimmed}`;
  }
  return trimmed;
};

const handleImageError = (e: { currentTarget: HTMLImageElement }) => {
  const img = e.currentTarget;
  const stage = img.dataset.fallbackStage || '';
  if (stage === 'default' || isDefaultPlaceholder(img.getAttribute('src') || '')) {
    img.onerror = null;
    return;
  }

  const fallback = getFallbackProductImage();
  const current = img.getAttribute('src') || '';
  if (stage === 'custom' || current === fallback || isDefaultPlaceholder(fallback)) {
    img.dataset.fallbackStage = 'default';
    img.onerror = null;
    img.src = DEFAULT_PRODUCT_PLACEHOLDER;
    return;
  }

  img.dataset.fallbackStage = 'custom';
  img.src = fallback;
};

export const getLazyImageProps = (src: string, alt: string) => ({
  src,
  alt,
  loading: 'lazy' as const,
  decoding: 'async' as const,
  onError: handleImageError,
});

/** Título visible: el API a veces manda name vacío y el texto en description. */
export const getProductDisplayName = (product: {
  name?: string | null;
  description?: string | null;
  sku?: string | null;
}): string => {
  if (!isBlank(product.name)) return product.name!.trim();
  if (!isBlank(product.description)) return product.description!.trim();
  if (!isBlank(product.sku)) return product.sku!.trim();
  return 'Producto';
};
